import React, { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { api, formatTL } from "@/lib/api";
import { TrendingUp, ShoppingBag, Wallet, XCircle } from "lucide-react";
import { ExportButton } from "@/components/admin/ExportButton";

const PERIODS = [{ d: 7, l: "7 Gün" }, { d: 30, l: "30 Gün" }, { d: 90, l: "90 Gün" }, { d: 365, l: "1 Yıl" }];
const PAY = { stripe: "Kredi Kartı", havale: "Havale/EFT", cari: "Cari Hesap" };
const STATUS = { pending: "Beklemede", confirmed: "Onaylandı", shipped: "Kargoda", delivered: "Teslim", cancelled: "İptal" };

const Kpi = ({ id, label, value, Icon, color }) => (
  <div data-testid={`report-kpi-${id}`} className="kz-card p-5">
    <Icon size={18} className={`${color} mb-2`}/>
    <div className="text-xs uppercase tracking-wider text-ink-500">{label}</div>
    <div className="font-display font-black text-2xl">{value}</div>
  </div>
);

function RankTable({ title, rows, cols, testid }) {
  return (
    <div className="kz-card" data-testid={testid}>
      <div className="px-4 py-3 border-b border-ink-200 font-display font-black text-sm uppercase tracking-wider">{title}</div>
      {rows.length === 0 ? <div className="p-6 text-sm text-ink-500 text-center">Bu dönemde veri yok</div> : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-ink-100/50" : ""}>
                <td className="px-4 py-2 w-8 font-mono text-xs text-ink-500">{i + 1}</td>
                {cols.map(c => <td key={c.k} className={`px-2 py-2 ${c.cls || ""}`}>{c.f ? c.f(r) : r[c.k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ReportsTab() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState(null);

  useEffect(() => { setR(null); api.get("/admin/reports", { params: { days } }).then(x => setR(x.data)); }, [days]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex border border-ink-200">
          {PERIODS.map(p => (
            <button key={p.d} data-testid={`report-period-${p.d}`} onClick={() => setDays(p.d)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${days === p.d ? "bg-ink-900 text-white" : "bg-white hover:bg-ink-100"}`}>{p.l}</button>
          ))}
        </div>
        <ExportButton path="/admin/export/orders" params={{ days }} filename="siparisler.xlsx" label="Bu Dönemin Siparişleri (Excel)" testid="report-export-orders"/>
      </div>

      {!r ? <div className="text-ink-500">Rapor hazırlanıyor...</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi id="revenue" label="Ciro (KDV dahil)" value={formatTL(r.totals.revenue)} Icon={TrendingUp} color="text-green-600"/>
            <Kpi id="orders" label="Sipariş" value={r.totals.orders} Icon={ShoppingBag} color="text-brand"/>
            <Kpi id="avg" label="Ortalama Sepet" value={formatTL(r.totals.avg_order)} Icon={Wallet} color="text-b2b"/>
            <Kpi id="cancelled" label="İptal" value={r.totals.cancelled} Icon={XCircle} color="text-red-600"/>
          </div>

          <div className="kz-card p-5" data-testid="report-revenue-chart">
            <div className="flex justify-between mb-4">
              <h3 className="font-display font-black">{r.granularity === "month" ? "Aylık" : "Günlük"} Ciro</h3>
              <span className="text-xs text-ink-500">Tahsil edilen: <b className="text-ink-900">{formatTL(r.totals.paid_revenue)}</b></span>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={r.series}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF5A00" stopOpacity={0.35}/><stop offset="100%" stopColor="#FF5A00" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid stroke="#E4E4E7" vertical={false}/>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                  <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}B` : v}/>
                  <Tooltip formatter={(v, n) => n === "revenue" ? [formatTL(v), "Ciro"] : [v, "Sipariş"]}/>
                  <Area type="monotone" dataKey="revenue" stroke="#FF5A00" strokeWidth={2} fill="url(#rev)"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="kz-card p-5" data-testid="report-orders-chart">
              <h3 className="font-display font-black mb-4">Sipariş Adedi</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={r.series}>
                    <CartesianGrid stroke="#E4E4E7" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30}/>
                    <Tooltip formatter={(v) => [v, "Sipariş"]}/>
                    <Bar dataKey="orders" fill="#18181B"/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="kz-card p-5" data-testid="report-payment-breakdown">
              <h3 className="font-display font-black mb-4">Ödeme Yöntemi & Durum</h3>
              <div className="space-y-3">
                {r.by_payment.length === 0 && <div className="text-sm text-ink-500">Veri yok</div>}
                {r.by_payment.map(p => {
                  const pct = r.totals.revenue ? (p.total / r.totals.revenue) * 100 : 0;
                  return (
                    <div key={p.method}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-semibold">{PAY[p.method] || p.method} · {p.count}</span><span>{formatTL(p.total)}</span></div>
                      <div className="h-2 bg-ink-100"><div className="h-2 bg-brand" style={{ width: `${pct}%` }}/></div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-ink-200">
                {r.by_status.map(s => <span key={s.status} className="text-xs bg-ink-100 px-2 py-1"><b>{STATUS[s.status] || s.status}:</b> {s.count}</span>)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RankTable testid="report-top-products" title="En Çok Satan Ürünler" rows={r.top_products} cols={[
              { k: "name", f: x => <div><div className="font-semibold line-clamp-1">{x.name}</div><div className="text-[11px] font-mono text-ink-500">{x.sku}</div></div> },
              { k: "quantity", cls: "text-right whitespace-nowrap", f: x => `${x.quantity} adet` },
              { k: "revenue", cls: "text-right font-bold whitespace-nowrap pr-4", f: x => formatTL(x.revenue) },
            ]}/>
            <RankTable testid="report-top-customers" title="En Çok Alan Bayiler / Müşteriler" rows={r.top_customers} cols={[
              { k: "name", f: x => <div><div className="font-semibold">{x.name}</div><div className="text-[11px] text-ink-500">{x.email}</div></div> },
              { k: "orders", cls: "text-right whitespace-nowrap", f: x => `${x.orders} sipariş` },
              { k: "revenue", cls: "text-right font-bold whitespace-nowrap pr-4", f: x => formatTL(x.revenue) },
            ]}/>
          </div>
        </>
      )}
    </div>
  );
}
